import { useContext, useEffect, useState } from 'react';

import { arrayMove } from '@dnd-kit/sortable';

import { useAddCollectionItem } from './useAddCollectionItem';
import { useCollection } from './useCollection';
import { useCollectionItemsData } from './useCollectionItemsData';
import { useCollectionSave } from './useCollectionSave';
import { useDeleteCollectionItems } from './useDeleteCollectionItems';
import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import type { ApiRequestProps } from '../../../common/interface';
import type { CollectionItem, Collection } from '../../../common/type';
import isLegacyId from '../../../helpers/isLegacyId';
import type { ArticleResponse } from '../../../templates/article/type';
import type { ClassResponse } from '../../../templates/class/type';
import { useBeehiveQuery } from '../../beehive/hooks';

export interface CollectionControllerProps extends ApiRequestProps {
  id: string;
  provider?: string;
  userId?: string;
}

export type SavePlaylist = (
  payload: Partial<Collection> & {
    deletedItems?: string[];
  }
) => Promise<unknown[]>;

export interface CollectionEntryItem {
  collectionData: CollectionItem | undefined;
  beehiveData: ClassResponse | ArticleResponse | undefined;
}

const sortItems = (items: CollectionEntryItem[]) => {
  return items.sort((first, second) => {
    return (
      (first?.collectionData?.position || 0) -
      (second?.collectionData?.position || 0)
    );
  });
};

export const usePlaylistController = (params: CollectionControllerProps) => {
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);
  const { isUserLoading } = useContext(LoginContext);
  const { baseURL, id } = params;
  const [reorderedItems, setReorderedItems] = useState<CollectionItem[]>([]);

  const parsedBaseURL = baseURL || COMMUNITY_FUNCTIONS_URL;

  const [orderedItems, setOrderedItems] = useState<CollectionEntryItem[]>([]);

  const {
    data: playlistData,
    isLoading: playlistIsLoading,
    isRefetching: playlistIsRefetching,
  } = useCollection({
    baseURL,
    id,
    enabled: !!id,
  });

  const itemKeys =
    playlistData?.collection_items?.map(item => item.item_key) || [];

  const {
    data: collectionItemsData,
    isLoading: collectionItemsIsLoading,
    isRefetching: collectionItemsIsRefetching,
  } = useCollectionItemsData({
    baseURL,
    id,
    itemKeys,
  });

  const regularIds = itemKeys.filter(key => !isLegacyId(key));
  const legacyIds = itemKeys.filter(key => isLegacyId(key));

  const { data: regularBeehiveData, isLoading: isRegularLoading } =
    useBeehiveQuery({
      baseURL,
      params: { 'fields.id': regularIds.join(',') },
      enabled: regularIds.length > 0,
    });

  const { data: legacyBeehiveData, isLoading: isLegacyLoading } =
    useBeehiveQuery({
      baseURL,
      params: { 'fields.legacyid': legacyIds.join(',') },
      enabled: legacyIds.length > 0,
    });

  const beehiveIsLoading = isRegularLoading || isLegacyLoading;
  const beehiveItemsResponse = [
    ...(regularBeehiveData?.items || []),
    ...(legacyBeehiveData?.items || []),
  ];

  const {
    isPending: isReorderLoading,
    mutateAsync: saveReorderItems,
    isError: isReorderError,
    error: reorderError,
    reset: reorderReset,
  } = useAddCollectionItem({
    baseURL: parsedBaseURL,
    id,
  });

  const {
    isPending: isEditLoading,
    mutateAsync: editPlaylist,
    isError: isSaveError,
    error: saveError,
    reset: saveReset,
  } = useCollectionSave({
    baseURL: parsedBaseURL,
    id,
  });

  const {
    isPending: deleteIsLoading,
    mutateAsync: deleteItems,
    isError: isDeleteError,
    error: deleteError,
    reset: deleteReset,
  } = useDeleteCollectionItems({
    baseURL,
    id,
  });

  const populateItems = () => {
    if (!collectionItemsData || !beehiveItemsResponse) return;

    let newItems: CollectionEntryItem[] = collectionItemsData.map(
      collectionData => {
        const beehiveData: ClassResponse | ArticleResponse | undefined =
          beehiveItemsResponse.find(
            entry =>
              (entry as ClassResponse | ArticleResponse)?.sys?.id ===
              collectionData?.item_key
          );

        return {
          collectionData,
          beehiveData,
        };
      }
    );

    newItems = sortItems(newItems);

    setOrderedItems(newItems);
  };

  useEffect(() => {
    populateItems();
  }, [
    collectionItemsIsLoading,
    beehiveIsLoading,
    playlistIsRefetching,
    collectionItemsIsRefetching,
  ]);

  const revertChanges = () => {
    populateItems();
  };

  const reorderItems = (prevId: string, nextId: string) => {
    if (prevId !== nextId) {
      setOrderedItems(oldItems => {
        let newItems = [...oldItems];
        const oldIndex = newItems.findIndex(
          item => item?.collectionData?.item_key === prevId
        );
        const newIndex = newItems.findIndex(
          item => item?.collectionData?.item_key === nextId
        );

        newItems = arrayMove(newItems, oldIndex, newIndex);

        const changedItems: CollectionItem[] = [];

        newItems = newItems.map((item, idx) => {
          if (typeof item?.collectionData?.position !== 'number') return item;

          // Stage item for change if user rearrange action detected
          if (item.collectionData.position !== idx) {
            changedItems.push({
              ...item.collectionData,
              position: idx,
            });
          }

          // Update item "position" entry in the API
          return {
            ...item,
            collectionData: {
              ...item.collectionData,
              position: idx,
            },
          };
        });

        setReorderedItems(changedItems);

        newItems = sortItems(newItems);

        return newItems;
      });
    }
  };

  const savePlaylist: SavePlaylist = async ({ deletedItems, ...payload }) => {
    const promises: Promise<unknown>[] = [];
    if (Object.keys(payload).length > 0) {
      promises.push(editPlaylist(payload));
    }
    if (reorderedItems.length > 0) {
      promises.push(saveReorderItems(reorderedItems));
    }
    if (deletedItems && deletedItems?.length > 0) {
      promises.push(deleteItems(deletedItems));
    }

    return Promise.all(promises);
  };

  return {
    isLoading:
      isUserLoading ||
      isEditLoading ||
      isReorderLoading ||
      playlistIsLoading ||
      deleteIsLoading ||
      collectionItemsIsLoading ||
      beehiveIsLoading,
    isUpdateError: isSaveError || isReorderError || isDeleteError,
    updateErrorReset: () => {
      saveReset();
      reorderReset();
      deleteReset();
    },
    updateError: saveError || reorderError || deleteError,
    playlistData,
    collectionItems: orderedItems,
    reorderItems,
    savePlaylist,
    revertChanges,
  };
};
